import copyToClipboard from "copy-to-clipboard"
import { observer } from "mobx-react"
import { getSnapshot } from "mobx-state-tree"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate"
import { EMPTY_OBJECT_ID } from "modules/common/constants"
import { routes } from "modules/common/routes"
import { trimStart } from "modules/common/services/strings"
import {
  mapQuestion,
  MessageType,
  OrderQuestionSnapshotType,
  SaveQuestion,
} from "modules/orders-manage/models/order-question"
import { parse } from "query-string"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { SystemColumns } from "../models/board"
import {
  DUE_LABELS,
  QuestionStatuses,
  STATUS_LABELS,
} from "../models/list"
import { OrderQuestionStoreType, StoredPortfolioId } from "../models/store"
import { BoardFilters, FilterItem, MultiFilter, shortName } from "./BoardFilters"
import { QuestionDialog } from "./QuestionDialog"
import { QuestionGridRow, QuestionsGrid } from "./QuestionsGrid"
import styles from "./QuestionsList.module.scss"

const STATUS_CLASS: TStringMap<string> = {
  [QuestionStatuses.work]: styles.work,
  [QuestionStatuses.review]: styles.review,
  [QuestionStatuses.expired]: styles.expired,
  [QuestionStatuses.done]: styles.done,
}

/** Поиск в фильтре «Колонка»: по названию колонки и доски */
const matchColumn = (query: string, item: FilterItem) => {
  const q = query.trim().toLowerCase()
  return item.label.toLowerCase().includes(q) || String(item.board ?? "").toLowerCase().includes(q)
}

/** Длительность выезда карточки — та же, что у transition в QuestionsList.module.scss */
const PANEL_ANIMATION_MS = 250

/** Номер задания вида 0099-24 или 0099-24-1: такой поиск сразу открывает задание, как ключ в Jira */
const NUMBER_PATTERN = /^\S+-\d+(-\d+)*$/

/**
 * Список заданий по всем доскам — как поиск задач в Jira: сверху фильтры, слева строки,
 * справа карточка выбранного задания. Выбранное задание — в адресе: ссылкой можно поделиться
 */
export const QuestionsList: React.FC<QuestionsListProps> = observer((props) => {
  const { store, canManage, canAddQuestion, currentUserId, questionId } = props
  const list = store.list
  const navigate = useStableNavigate()

  const [editable, setEditable] = useState<SaveQuestion | null>(null)
  const [current, setCurrent] = useState<OrderQuestionSnapshotType | null>(null)
  /** Карточка выехала. Данные карточки живут дольше: при закрытии она уезжает с ними */
  const [shown, setShown] = useState(false)
  /** Карточка уже открыта и сменилась другой — новая въезжает с короткой анимацией */
  const [swapped, setSwapped] = useState(false)
  const shownRef = useRef(false)
  const closeTimer = useRef(0)
  const dirty = useRef(false)

  const onDirtyChange = useCallback((value: boolean) => {
    dirty.current = value
  }, [])

  // в карточке есть несохранённое — переход к другой строке спрашиваем, как закрытие окна
  const confirmLeave = useCallback(
    () => !dirty.current || window.confirm("У вас есть несохранённые изменения. Перейти без сохранения?"),
    []
  )

  const select = useCallback(
    (id: string) => {
      if (id === questionId || !confirmLeave()) {
        return
      }

      dirty.current = false
      navigate(routes.orders.orderQuestionsList(id))
    },
    [questionId, confirmLeave, navigate]
  )

  /** Карточка выезжает справа: сначала рисуется за краем, на следующем кадре едет на место */
  const showPanel = useCallback((question: OrderQuestionSnapshotType, value: SaveQuestion) => {
    window.clearTimeout(closeTimer.current)
    // при первом открытии выезжает сама панель — сменой карточки это не считаем
    setSwapped(shownRef.current)
    setCurrent(question)
    setEditable(value)
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        shownRef.current = true
        setShown(true)
      })
    )
  }, [])

  /** Карточка уезжает вправо и только потом пропадает: до конца анимации данные нужны */
  const hidePanel = useCallback(() => {
    shownRef.current = false
    setShown(false)
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => {
      setEditable(null)
      setCurrent(null)
    }, PANEL_ANIMATION_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  const closePanel = useCallback(() => {
    dirty.current = false
    hidePanel()
    questionId && navigate(routes.orders.orderQuestionsList())
  }, [questionId, navigate, hidePanel])

  // справочники для фильтров и карточки: доска грузит их сама, список может открыться первым.
  // В dev StrictMode эффект срабатывает дважды — второй раз справочники не запрашиваем
  const dictionaries = useRef(false)
  useEffect(() => {
    if (dictionaries.current) {
      return
    }

    dictionaries.current = true
    store.employee.isEmpty && store.employee.load()
    store.orders.isEmpty && store.orders.load()
  }, [store])

  // ссылка со страницы «Сотрудники» или поиск с доски приходят в хэше:
  // #employerId=…&status=… или #q=…. Применяем и убираем из адреса — дальше фильтры живут в списке
  // второй проход StrictMode увидел бы адрес уже без хэша и затёр ссылку сохранёнными фильтрами
  const linkApplied = useRef(false)
  /** Список открыли поиском с доски: курсор остаётся в поле, набор продолжается */
  const [searchFocus, setSearchFocus] = useState(false)
  useEffect(() => {
    if (linkApplied.current) {
      return
    }

    linkApplied.current = true
    const params = parse(trimStart("#", window.location.hash))
    const employerId = (params["employerId"] as string) || ""
    const status = (params["status"] as string) || ""
    const query = (params["q"] as string) || ""

    // поиск идёт по доске, с которой пришли: в этой вкладке она в сторе, при открытии ссылки — запомненная
    list.setHomeBoard(store.portfolioId || StoredPortfolioId())

    if (employerId || query) {
      list.applyLink({ employerId, status, query })
      setSearchFocus(!!query)
      navigate(window.location.pathname, { replace: true })
    } else {
      list.restore()
    }

    list.load()
    // только при открытии списка
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // выбранное задание из адреса: грузим целиком, со всеми полями карточки
  useEffect(() => {
    let cancelled = false

    if (!questionId) {
      hidePanel()
      return
    }

    const open = async () => {
      const loaded = await store.getById(questionId)

      if (cancelled) {
        return
      }

      if (!loaded) {
        navigate(routes.orders.orderQuestionsList(), { replace: true })
        return
      }

      const question = getSnapshot(store.question) as OrderQuestionSnapshotType
      showPanel(question, mapQuestion(question))
    }

    open()

    return () => {
      cancelled = true
    }
  }, [questionId, store, navigate, showPanel, hidePanel])

  // поиск по номеру задания сразу открывает его — как ввод ключа задачи в Jira
  useEffect(() => {
    const query = list.query.trim().toLowerCase()

    if (questionId || !NUMBER_PATTERN.test(query)) {
      return
    }

    const found = list.items.find((r) => r.number.toLowerCase() === query)
    found && select(found.id)
    // строки меняются целиком при каждой загрузке — этого достаточно
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.items.length, list.loading])

  const board = current ? list.board(current.portfolioId ?? "") : null

  const columns = useMemo(() => (board ? getSnapshot(board.columns).slice() : []), [board])
  const tags = useMemo(() => (board ? getSnapshot(board.tags).slice() : []), [board])
  const members = useMemo(
    () => (board && board.members ? board.members.map((m) => ({ id: m.id, name: m.name })) : null),
    [board]
  )

  /** Новое задание: на выбранной в фильтре доске, а если их несколько — на общей */
  const openNew = useCallback(() => {
    if (!confirmLeave()) {
      return
    }

    // новое задание — на доске, с которой пришли в список
    const boardId = list.homeBoard
    const target = list.board(boardId)
    const empty = store.emptyQuestion()

    const question: OrderQuestionSnapshotType = {
      ...empty,
      portfolioId: boardId,
      columnId: target && target.columns.length > 0 ? target.columns[0].key : SystemColumns.inWork,
      stopDate: boardId ? "" : empty.stopDate,
      stopDay: boardId ? "" : empty.stopDay,
    }

    dirty.current = false
    showPanel(question, mapQuestion(question))
  }, [list, store, confirmLeave, showPanel])

  const createChild = useCallback(
    (parent: OrderQuestionSnapshotType) => {
      const target = list.board(parent.portfolioId ?? "")
      const question: OrderQuestionSnapshotType = {
        ...store.emptyQuestion(),
        portfolioId: parent.portfolioId ?? "",
        columnId: target && target.columns.length > 0 ? target.columns[0].key : SystemColumns.inWork,
      }

      dirty.current = false
      showPanel(question, mapQuestion(question, null, parent))
    },
    [list, store, showPanel]
  )

  const submit = useCallback(
    async (value: SaveQuestion) => {
      const success = await store.save(value, false)

      if (!success) {
        return false
      }

      dirty.current = false
      list.load()

      // новое задание сохранили — его id знает только сервер, карточку закрываем;
      // прежнее — перечитываем, чтобы карточка показала сохранённое
      if (!value.id || value.id === EMPTY_OBJECT_ID || !questionId) {
        closePanel()
      } else if (await store.getById(questionId)) {
        const question = getSnapshot(store.question) as OrderQuestionSnapshotType
        setCurrent(question)
        setEditable(mapQuestion(question))
      }

      return true
    },
    [store, list, questionId, closePanel]
  )

  const remove = useCallback(
    async (value: SaveQuestion) => {
      const success = await store.remove([{ id: value.id }], false)

      if (success) {
        list.load()
        closePanel()
      }
    },
    [store, list, closePanel]
  )

  /**
   * Карточка в списке — снимок задания: после того как сервер поменял готовность или ленту,
   * подставляем свежий. Форму не трогаем — несохранённые правки полей остаются
   */
  const refreshCurrent = useCallback(() => {
    store.question.id && setCurrent(getSnapshot(store.question) as OrderQuestionSnapshotType)
  }, [store])

  const setReadiness = useCallback(
    async (id: string, ready: boolean, employerId?: string) => {
      const result = await store.setReadiness(id, ready, employerId)
      refreshCurrent()
      list.load()
      return result
    },
    [store, list, refreshCurrent]
  )

  const addMessage = useCallback(
    async (id: string, message: MessageType, fileIds?: string[]) => {
      const result = await store.saveMessage(id, message, fileIds)
      refreshCurrent()
      // результат отметил готовность — статус строки в списке мог поменяться
      message.isResult && list.load()
      return result
    },
    [store, list, refreshCurrent]
  )

  const deleteMessage = useCallback(
    async (id: string, messageId: string) => {
      const result = await store.deleteMessage(id, messageId)
      refreshCurrent()
      return result
    },
    [store, refreshCurrent]
  )

  const share = useCallback(
    (id: string) => {
      const link = window.location.origin + routes.orders.orderQuestionsList(id)

      copyToClipboard(link, { format: "text/plain" })
        ? store.notify.success("Ссылка на задание скопирована")
        : store.notify.error("Не удалось скопировать ссылку")
    },
    [store]
  )

  const employerName = useMemo(() => {
    if (!list.employerId) {
      return ""
    }

    const known = store.employee.employee.find((e) => e.id === list.employerId)
    // в плашке места мало: «Иванов И.И.», как в тегах фильтров по людям
    return shortName(known ? known.name : list.employerName || "сотрудник")
    // справочник догружается после первой отрисовки
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.employerId, list.employerName, store.employee.employee.length])

  /** Пункты фильтра «Доска»: доски пользователя с числом открытых заданий на каждой */
  const boardItems = useMemo(
    () => list.boards.map((b) => ({ id: b.id, label: b.name, count: list.boardCounts.get(b.id) ?? 0 })),
    // доски и числа меняются вместе с загрузкой списка
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.boards, list.boards.length, list.boardCounts.size, list.loading]
  )

  // подсвечиваем, когда ищем не только по своей доске
  const boardsChanged =
    list.allBoards || (list.boardIds.length > 0 && !(list.boardIds.length === 1 && list.boardIds[0] === list.homeBoard))

  // запомненной доски уже нет (удалили или из неё вышли) — ищем по общей
  useEffect(() => {
    if (list.loaded && !list.allBoards && list.homeBoard && list.boards.length > 0 && !list.board(list.homeBoard)) {
      list.setHomeBoard("")
      list.load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.loaded, list.boards.length, list.homeBoard, list.allBoards])

  /**
   * Пункты фильтра «Колонка»: колонки своей доски, при «По всем доскам» — и всех остальных.
   * У каждой — доска и сколько в ней заданий при остальных фильтрах
   */
  const columnItems = useMemo(
    () =>
      list.boards
        .filter((b) => list.selectedBoards.indexOf(b.id) >= 0)
        .flatMap((b) =>
          b.columns.map((c) => {
            const id = `${b.id}|${c.key}`
            return { id, label: c.name, board: b.name, count: list.columnCounts.get(id) ?? 0 }
          })
        ),
    // доски и числа меняются вместе с загрузкой списка
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.boards, list.boards.length, list.allBoards, list.boardIds.length, list.homeBoard, list.columnCounts.size, list.loading]
  )

  // строки таблицы — простые значения: грид не должен держать узлы MST, список перезагружается
  const gridRows: QuestionGridRow[] = list.items.map((row) => {
    const people = [...row.employers, ...row.implementers].filter(
      (p, i, all) => all.findIndex((x) => x.id === p.id) === i
    )
    const column = list.column(row.portfolioId, row.columnId)

    return {
      id: row.id,
      number: row.number,
      title: row.title,
      parentId: row.parentId,
      parentNumber: row.parentNumber,
      parentTitle: row.parentTitle,
      orderName: row.order && row.order.name.trim() ? row.order.name : "—",
      boardName: list.board(row.portfolioId)?.name ?? "",
      columnName: column ? column.name : "",
      columnColor: column ? column.color : "",
      stopDay: row.stopDay,
      expired: row.status === QuestionStatuses.expired,
      created: row.created,
      people: people.map((p) => p.name).join(", "),
    }
  })

  // строки сжимаются вместе с выездом карточки, а не когда она уже уехала
  const panel = shown

  return (
    <div className={styles.list}>
      <div className={styles.toolbar}>
        <BoardFilters
          orders={store.orders}
          employers={store.employee}
          query={list.query}
          orderIds={list.orderIds.slice()}
          authorIds={list.authorIds.slice()}
          employerIds={list.employerIds.slice()}
          implementerIds={list.implementerIds.slice()}
          setQuery={(value) => {
            list.setQuery(value)
            list.onQueryChanged()
          }}
          setOrderIds={list.setOrderIds}
          setAuthorIds={list.setAuthorIds}
          setEmployerIds={list.setEmployerIds}
          setImplementerIds={list.setImplementerIds}
          searchAutoFocus={searchFocus}
        >
          <PlanrButton
            type="lightblueish"
            size="small"
            icon="general-chevron-left"
            title="Вернуться в трекер задач"
            onClick={() => confirmLeave() && navigate(routes.orders.orderQuestions)}
          >
            К трекеру
          </PlanrButton>
          {canAddQuestion && (
            <PlanrButton type="primary" size="small" icon="general-plus-small" onClick={openNew}>
              Задание
            </PlanrButton>
          )}
        </BoardFilters>

        <div className={styles.chips}>
          {employerName && (
            <span className={styles.employer} title="Задания, где сотрудник ответственный или исполнитель">
              Задания сотрудника: {employerName}
              <button type="button" className={styles.clear} title="Убрать" onClick={list.clearEmployer}>
                ×
              </button>
            </span>
          )}

          {/* статус приходит только ссылкой с бейджа сотрудника: число строк совпадает с бейджем */}
          {list.statuses.length > 0 && (
            <span className={`${styles.employer} ${STATUS_CLASS[list.statuses[0]] ?? ""}`}>
              {list.statuses.map((status) => STATUS_LABELS[status] ?? status).join(", ")}
              <button type="button" className={styles.clear} title="Убрать" onClick={list.clearStatuses}>
                ×
              </button>
            </span>
          )}

          {/* доски: по умолчанию та, с которой пришли; галочка сверху — искать по всем */}
          <MultiFilter
            what="Доска"
            many="Досок"
            placeholder="Поиск доски"
            popoverClassName={styles.columnsPopover}
            items={boardItems}
            selected={list.allBoards ? [] : list.selectedBoards}
            emptyLabel={list.allBoards ? "Все доски" : undefined}
            active={boardsChanged}
            label={(item) => item.label}
            tag={(item) => item.label}
            match={matchColumn}
            header={
              <button
                type="button"
                role="checkbox"
                aria-checked={list.allBoards}
                className={styles.allBoardsToggle}
                onClick={() => list.setAllBoards(!list.allBoards)}
              >
                <GeneralIcon type={list.allBoards ? "general-checkbox" : "general-zero-checkbox"} />
                По всем доскам
              </button>
            }
            hint={(item) => <span className={styles.count}>{item.count}</span>}
            onChange={list.setBoards}
          />

          <MultiFilter
            what="Колонка"
            many="Колонок"
            placeholder="Поиск колонки"
            popoverClassName={styles.columnsPopover}
            items={columnItems}
            selected={list.columns.slice()}
            label={(item) => item.label}
            tag={(item) => item.label}
            match={matchColumn}
            hint={(item) => (
              <span className={styles.columnHint}>
                {list.selectedBoards.length > 1 && <span className={styles.columnBoard}>{item.board}</span>}
                <span className={styles.count}>{item.count}</span>
              </span>
            )}
            onChange={list.setColumns}
          />


          <span className={styles.divider} />

          {Object.keys(DUE_LABELS)
            .filter((key) => key)
            .map((key) => (
              <button
                key={key}
                type="button"
                className={`${styles.chip} ${list.due === key ? styles.on : ""}`}
                aria-pressed={list.due === key}
                onClick={() => list.setDue(list.due === key ? "" : key)}
              >
                {DUE_LABELS[key]}
              </button>
            ))}

        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.table}>
          <QuestionsGrid
            rows={gridRows}
            selectedId={questionId}
            wide={!panel}
            sort={list.sort}
            desc={list.desc}
            loaded={list.loaded}
            onSelect={select}
            onSort={list.setSortTo}
            onNearEnd={list.loadMore}
          />

          <div className={styles.footer}>
            {list.loaded && `Показано ${list.items.length} из ${list.total}`}
          </div>

          <LocalLoader active={list.loading} />
        </div>

        {editable && current && (
          <div className={`${styles.panel} ${shown ? styles.panelOpen : ""}`}>
            {/* ключ по заданию: при смене задания обёртка новая, и анимация запускается заново */}
            <div key={current.id || "new"} className={`${styles.panelInner} ${swapped ? styles.swap : ""}`}>
            <QuestionDialog
              key={current.id || "new"}
              inline
              value={editable}
              question={current}
              columns={columns}
              tags={tags}
              employee={store.employee}
              orders={store.orders}
              baseUrl={store.baseUrl}
              currentUserId={currentUserId}
              canAddQuestion={canAddQuestion && canManage}
              portfolioMembers={members}
              onClose={closePanel}
              onShare={share}
              onSubmit={submit}
              onRemove={remove}
              onAddMessage={addMessage}
              onDeleteMessage={deleteMessage}
              boardName={board ? board.name : ""}
              onCreateChild={createChild}
              onOpenQuestion={(q: { id: string }) => select(q.id)}
              onSetReadiness={setReadiness}
              onDirtyChange={onDirtyChange}
              upload={store.uploadFile}
            />
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

interface QuestionsListProps {
  store: OrderQuestionStoreType
  canManage: boolean
  canAddQuestion: boolean
  currentUserId: string
  /** Выбранное задание из адреса */
  questionId: string
}
